<!-- src/components/GenericComponents/FeedbackToast.vue -->
<!-- 
  Elegant feedback toast for all submit/request actions
  - Non-blocking overlay
  - Dismissible by click
  - Auto-fades after 9 seconds
  - User can interact with everything else
-->
<template>
  <Teleport to="body">
    <transition name="feedback-toast">
      <div
        v-if="isVisible"
        class="feedback-toast-container"
        :class="[variant, { 'is-fading': isFading }]"
        @click="dismiss"
      >
        <div class="feedback-toast-content">
          <div class="feedback-icon" v-if="variant === 'success'">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                d="M20 6L9 17l-5-5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="feedback-icon" v-else-if="variant === 'error'">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M15 9l-6 6M9 9l6 6" stroke-linecap="round" />
            </svg>
          </div>
          <div class="feedback-icon" v-else>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4M12 8h.01" stroke-linecap="round" />
            </svg>
          </div>
          <span class="feedback-message">{{ message }}</span>
          <button
            class="feedback-dismiss"
            @click.stop="dismiss"
            aria-label="Dismiss"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M18 6L6 18M6 6l12 12" stroke-linecap="round" />
            </svg>
          </button>
        </div>
        <div
          class="feedback-progress"
          :style="{ animationDuration: `${duration}ms` }"
        ></div>
      </div>
    </transition>
  </Teleport>
</template>

<script>
import { ref, watch, onUnmounted } from "vue";

export default {
  name: "FeedbackToast",
  props: {
    message: {
      type: String,
      default: "",
    },
    variant: {
      type: String,
      default: "info", // 'success', 'error', 'info'
      validator: (value) => ["success", "error", "info"].includes(value),
    },
    duration: {
      type: Number,
      default: 9000, // 9 seconds
    },
    show: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["dismissed"],
  setup(props, { emit }) {
    const isVisible = ref(false);
    const isFading = ref(false);
    let fadeTimeout = null;
    let hideTimeout = null;

    const startFadeOut = () => {
      // Start fading 1 second before hide
      isFading.value = true;
    };

    const dismiss = () => {
      isFading.value = true;
      clearTimeout(fadeTimeout);
      clearTimeout(hideTimeout);
      setTimeout(() => {
        isVisible.value = false;
        isFading.value = false;
        emit("dismissed");
      }, 300);
    };

    const showToast = () => {
      isVisible.value = true;
      isFading.value = false;

      // Clear any existing timeouts
      clearTimeout(fadeTimeout);
      clearTimeout(hideTimeout);

      // Start fade effect 1 second before auto-hide
      fadeTimeout = setTimeout(startFadeOut, props.duration - 1000);

      // Auto-hide after duration
      hideTimeout = setTimeout(() => {
        isVisible.value = false;
        isFading.value = false;
        emit("dismissed");
      }, props.duration);
    };

    watch(
      () => props.show,
      (newVal) => {
        if (newVal && props.message) {
          showToast();
        } else if (!newVal) {
          isVisible.value = false;
          isFading.value = false;
          clearTimeout(fadeTimeout);
          clearTimeout(hideTimeout);
        }
      },
      { immediate: true }
    );

    onUnmounted(() => {
      clearTimeout(fadeTimeout);
      clearTimeout(hideTimeout);
    });

    return {
      isVisible,
      isFading,
      dismiss,
    };
  },
};
</script>

<style scoped>
.feedback-toast-container {
  position: fixed;
  bottom: calc(var(--largeSpace) + 60px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  pointer-events: auto;
  cursor: pointer;
  max-width: calc(100vw - var(--doubleSpace));
  width: auto;
  min-width: 280px;
}

.feedback-toast-content {
  display: flex;
  align-items: center;
  gap: var(--space);
  padding: var(--space) var(--largeSpace);
  background: rgba(20, 20, 25, 0.92);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: var(--border-radius-pill);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  transition: all 0.3s ease;
}

.feedback-toast-container:hover .feedback-toast-content {
  background: rgba(25, 25, 30, 0.95);
  border-color: rgba(255, 255, 255, 0.15);
}

.feedback-icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.feedback-icon svg {
  width: 20px;
  height: 20px;
}

/* Variant colors */
.success .feedback-icon {
  color: var(--green, #4ade80);
}

.error .feedback-icon {
  color: var(--red, #f87171);
}

.info .feedback-icon {
  color: var(--purple-light, #a78bfa);
}

.feedback-message {
  flex: 1;
  font-size: var(--smallFontSize);
  color: var(--text-white);
  line-height: 1.4;
  text-align: left;
}

.feedback-dismiss {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: var(--grey);
  transition: all 0.2s ease;
  padding: 0;
  margin-left: var(--smallSpace);
}

.feedback-dismiss:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--text-white);
}

.feedback-dismiss svg {
  width: 14px;
  height: 14px;
}

/* Progress bar */
.feedback-progress {
  position: absolute;
  bottom: 0;
  left: var(--largeSpace);
  right: var(--largeSpace);
  height: 2px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 1px;
  overflow: hidden;
}

.feedback-progress::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background: var(--purple-light, #a78bfa);
  animation: progress-shrink linear forwards;
  animation-duration: inherit;
  transform-origin: left;
}

.success .feedback-progress::after {
  background: var(--green, #4ade80);
}

.error .feedback-progress::after {
  background: var(--red, #f87171);
}

@keyframes progress-shrink {
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
}

/* Fading state */
.is-fading .feedback-toast-content {
  opacity: 0.7;
}

/* Enter/Leave transitions */
.feedback-toast-enter-active {
  animation: toast-enter 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.feedback-toast-leave-active {
  animation: toast-leave 0.3s cubic-bezier(0.4, 0, 1, 1);
}

@keyframes toast-enter {
  0% {
    opacity: 0;
    transform: translateX(-50%) translateY(20px) scale(0.95);
  }
  100% {
    opacity: 1;
    transform: translateX(-50%) translateY(0) scale(1);
  }
}

@keyframes toast-leave {
  0% {
    opacity: 1;
    transform: translateX(-50%) translateY(0) scale(1);
  }
  100% {
    opacity: 0;
    transform: translateX(-50%) translateY(10px) scale(0.98);
  }
}

/* Mobile adjustments */
@media (max-width: 480px) {
  .feedback-toast-container {
    bottom: var(--largeSpace);
    left: var(--space);
    right: var(--space);
    transform: none;
    max-width: none;
    min-width: auto;
  }

  .feedback-toast-enter-active {
    animation: toast-enter-mobile 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .feedback-toast-leave-active {
    animation: toast-leave-mobile 0.3s cubic-bezier(0.4, 0, 1, 1);
  }

  @keyframes toast-enter-mobile {
    0% {
      opacity: 0;
      transform: translateY(20px) scale(0.95);
    }
    100% {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @keyframes toast-leave-mobile {
    0% {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
    100% {
      opacity: 0;
      transform: translateY(10px) scale(0.98);
    }
  }
}
</style>
